import { useMemo, useState } from "react";
import { Button } from "../../components/ui/button.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Textarea } from "../../components/ui/textarea.jsx";
import { Calendar } from "lucide-react";

function toDateInputValue(date) {
  if (!date) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

const CALL_TYPES = [
  { key: "video", label: "Video" },
  { key: "voice", label: "Voice" },
];

export default function SchedulePanel({ defaultTitle = "Team sync", defaultCallType = "video", onSchedule }) {
  const now = useMemo(() => new Date(), []);
  const start = useMemo(() => {
    const d = new Date(now.getTime() + 15 * 60000);
    return d;
  }, [now]);
  const end = useMemo(() => new Date(start.getTime() + 30 * 60000), [start]);

  const [title, setTitle] = useState(`${defaultTitle}`);
  const [description, setDescription] = useState("");
  const [startAt, setStartAt] = useState(toDateInputValue(start));
  const [endAt, setEndAt] = useState(toDateInputValue(end));
  const [callType, setCallType] = useState(defaultCallType);
  const [confirmation, setConfirmation] = useState(null);

  const submit = (event) => {
    event.preventDefault();
    if (!startAt || !endAt) return;
    const payload = {
      title: title.trim() || "Neoin voice call",
      description: description.trim(),
      startAt,
      endAt,
      callType,
    };
    onSchedule?.(payload);
    setConfirmation(payload);
  };

  return (
    <div id="voice-schedule" className="glass-card rounded-3xl p-6 text-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary">
          <Calendar className="h-4 w-4" />
        </span>
        Schedule call
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Create a call link in advance and add it to your calendar. We will send reminders and preload the room with your agenda.
      </p>

      <form onSubmit={submit} className="mt-4 space-y-3 text-xs">
        <label className="block text-xs font-medium text-muted-foreground">
          Title
          <Input value={title} onChange={(event) => setTitle(event.target.value)} className="mt-1" />
        </label>
        <label className="block text-xs font-medium text-muted-foreground">
          Description
          <Textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} className="mt-1" placeholder="Agenda, attachments, or context" />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs font-medium text-muted-foreground">
            Starts
            <Input type="datetime-local" value={startAt} onChange={(event) => setStartAt(event.target.value)} className="mt-1" required />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            Ends
            <Input type="datetime-local" value={endAt} onChange={(event) => setEndAt(event.target.value)} className="mt-1" required />
          </label>
        </div>
        <div className="flex gap-2">
          {CALL_TYPES.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setCallType(option.key)}
              className={`rounded-full px-3 py-2 text-xs font-semibold transition ${callType === option.key ? "bg-primary text-primary-foreground" : "border border-border bg-background/50 text-muted-foreground hover:text-foreground"}`}
              aria-pressed={callType === option.key}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" size="sm">
            Save &amp; share link
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setConfirmation(null)}>
            Reset
          </Button>
        </div>
      </form>

      {confirmation && (
        <div className="mt-4 rounded-2xl border border-border bg-muted/40 p-4 text-xs text-muted-foreground">
          <div className="text-sm font-semibold text-foreground">{confirmation.title}</div>
          <div>{new Date(confirmation.startAt).toLocaleString()} ➝ {new Date(confirmation.endAt).toLocaleString()}</div>
          <div>Type: {confirmation.callType}</div>
          {confirmation.description && <div className="mt-2 text-muted-foreground">{confirmation.description}</div>}
        </div>
      )}
    </div>
  );
}
